/**
 * Disambiguation Question Component
 *
 * Collects a follow-up answer (name or ZIP) when the backend matched
 * multiple consumers on phone + DOB.
 * LoadingBar and navigation buttons are rendered by the parent layout.
 */

"use client";

import { useTranslations } from "@frontend/lib/next-intl-typed";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@frontend/shared/components/ui/form";
import { Input } from "@frontend/shared/components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  useInsuranceConnectorContext,
  useInsuranceConnectorSend,
} from "../insurance-connector-provider";
import { INSURANCE_STEP_FORM_ID } from "../step-config";

// Only `name` and `zip` are supported as interactive disambiguation prompts.
// `name` is normally resolved automatically server-side using the name we've
// already collected; the UI is a fallback. Any other disambiguation field
// returned by the backend is handled upstream by routing the user through as
// a new consumer rather than prompting.
const nameSchema = z.object({
  firstName: z
    .string()
    .trim()
    .min(1, "First name is required")
    .min(2, "First name must be at least 2 characters")
    .max(50, "First name must be less than 50 characters")
    .regex(/^[a-zA-Z\s'-]+$/, "First name contains invalid characters"),
  lastName: z
    .string()
    .trim()
    .min(1, "Last name is required")
    .min(2, "Last name must be at least 2 characters")
    .max(50, "Last name must be less than 50 characters")
    .regex(/^[a-zA-Z\s'-]+$/, "Last name contains invalid characters"),
});

const zipSchema = z.object({
  zip: z
    .string()
    .trim()
    .min(1, "ZIP code is required")
    .regex(/^[0-9]{5}$/, "ZIP code must be 5 digits"),
});

type NameFormValues = z.infer<typeof nameSchema>;
type ZipFormValues = z.infer<typeof zipSchema>;

const inputClassName =
  "rounded-full py-[31px] text-lg w-full px-5 border-transparent focus:border-transparent focus:ring-1 focus:ring-[#00A3FF] focus:outline-none focus-visible:ring-1 focus-visible:ring-[#00A3FF] focus-visible:ring-offset-0";

export function DisambiguationQuestion() {
  const t = useTranslations("InsuranceConnector.Disambiguation");
  const context = useInsuranceConnectorContext();
  const send = useInsuranceConnectorSend();

  const field = context.disambiguationField || "zip";

  // In practice only the ZIP form should render here: the machine
  // auto-disambiguates name-field multi-matches using the firstName/lastName
  // already collected on the name step, so `field === "name"` is only
  // reachable as a defensive fallback if the backend ever routes a name
  // disambiguation here without context having a name. Kept to avoid a
  // blank screen on that regression.
  if (field === "name") {
    return <NameForm onSubmit={(data) => send({ type: "SUBMIT", data })} />;
  }

  return (
    <ZipForm
      onSubmit={(data) => send({ type: "SUBMIT", data })}
      title={t("ZipTitle")}
    />
  );
}

function NameForm({
  onSubmit,
}: {
  onSubmit: (data: NameFormValues) => void;
}) {
  const t = useTranslations("InsuranceConnector.Disambiguation");
  const form = useForm<NameFormValues>({
    resolver: zodResolver(nameSchema),
    defaultValues: { firstName: "", lastName: "" },
  });

  return (
    <Form {...form}>
      <form
        id={INSURANCE_STEP_FORM_ID}
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-5 md:gap-8"
        noValidate
      >
        <h2 className="text-2xl md:text-[32px] leading-[120%] tracking-[-0.02em] font-semibold">
          {t("NameTitle")}
        </h2>

        <div className="flex flex-col md:flex-row gap-4 md:gap-8">
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel className="sr-only">{t("FirstNameLabel")}</FormLabel>
                <FormControl>
                  <Input
                    className={inputClassName}
                    placeholder={t("FirstNamePlaceholder")}
                    autoFocus
                    autoCapitalize="words"
                    autoCorrect="off"
                    autoComplete="given-name"
                    spellCheck={false}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel className="sr-only">{t("LastNameLabel")}</FormLabel>
                <FormControl>
                  <Input
                    className={inputClassName}
                    placeholder={t("LastNamePlaceholder")}
                    autoCapitalize="words"
                    autoCorrect="off"
                    autoComplete="family-name"
                    spellCheck={false}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </form>
    </Form>
  );
}

function ZipForm({
  onSubmit,
  title,
}: {
  onSubmit: (data: ZipFormValues) => void;
  title: string;
}) {
  const t = useTranslations("InsuranceConnector.Disambiguation");
  const form = useForm<ZipFormValues>({
    resolver: zodResolver(zipSchema),
    defaultValues: { zip: "" },
  });

  return (
    <Form {...form}>
      <form
        id={INSURANCE_STEP_FORM_ID}
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-4 md:gap-8"
        noValidate
      >
        <h2 className="text-2xl md:text-[32px] leading-[120%] tracking-[-0.02em] font-semibold">
          {title}
        </h2>

        <FormField
          control={form.control}
          name="zip"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel className="sr-only">{t("ZipCodeLabel")}</FormLabel>
              <FormControl>
                <Input
                  className={inputClassName}
                  placeholder={t("ZipCodePlaceholder")}
                  inputMode="numeric"
                  maxLength={5}
                  autoFocus
                  autoCapitalize="none"
                  autoCorrect="off"
                  autoComplete="postal-code"
                  spellCheck={false}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
}
